import React, { useEffect, useState } from 'react';
import './styles/globals.css';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Layout } from './components/Layout';
import Projects from './pages/Projects';
import Settings from './pages/Settings';
import Login from './pages/Login.tsx';
import Register from './pages/Register.tsx';
import { PrivateRoute } from './components/PrivateRoute';
import ActivateEmail from './pages/ActivateEmail.tsx';
import LandingRedirect from "@/pages/LandingRedirect";
import ForgotPasswordEmail from './pages/ForgotPasswordEmail.tsx';
import SourcesPage from "@/pages/vb/sources/SourcesPage";
import WorkbenchPage from "@/pages/workbench/WorkbenchPage";
import Disclaimer from './pages/Disclaimer.tsx';
import PaymentSuccessful from './pages/Payment.tsx';
import ProjectsList from "@/pages/ProjectsList.tsx";
import VbLayout from "@/components/vb/VbLayout";
import BuyerOverviewPage from "@/pages/vb/BuyerOverviewPage";
import BuyerCreatePage from "@/pages/vb/BuyerCreatePage";
import BuyerDetailPage from "@/pages/vb/BuyerDetailPage";
import RunPage from "@/pages/vb/RunPage";
import VerdictPage from "@/pages/vb/VerdictPage";
import PageviewTracker from "@/components/PageViewTracker.tsx";
import PasswordReset from "@/components/auth/PasswordReset.tsx";
import { UrlParamsCapture } from "@/components/UrlParamsCapture";
import api from "@/services/api";
import OnboardingPage from "@/pages/OnboardingPage";

interface BannerData {
  is_active: boolean;
  message: string;
  level: "info" | "warning" | "error";
}

export const BannerContext = React.createContext<BannerData | null>(null);

function App() {
  const [banner, setBanner] = useState<BannerData | null>(null);

  useEffect(() => {
    // Fetch banner configuration on app mount
    const fetchBanner = async () => {
      try {
        const response = await api.get<BannerData>("/system/banner/");
        if (response.data.is_active) {
          setBanner(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch system banner:", error);
        // Silently fail - banner is not critical for app functionality
      }
    };

    fetchBanner();
  }, []);

  return (
    <BannerContext.Provider value={banner}>
      <BrowserRouter>
        <UrlParamsCapture />
        <PageviewTracker />
        <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/sign-up" element={<Register />} />
        <Route path="/activate-account/:uid/:token" element={<ActivateEmail />} />
        <Route path="/forgot-password" element={<ForgotPasswordEmail />} />
        <Route path="/password-reset/:uid/:token" element={<PasswordReset />} />
        <Route path="/disclaimer" element={<Disclaimer />} />
        <Route path="/payment-success" element={<PaymentSuccessful />} />


        {/* Protected routes */}
        <Route element={<PrivateRoute />}>
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/" element={<Layout />}>
            {/* `/` is a resolver, not a screen (AITS-540): it redirects into
                the user's company. Everything that used to send people "home"
                still points here. */}
            <Route path="/" element={<LandingRedirect />} />
            <Route path="/reports/:projectId" element={<Projects />} />
            <Route path="/reports/:projectId/:reportId" element={<Projects />} />
            <Route path="/projects" element={<ProjectsList />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/reports/:projectId/sources" element={<SourcesPage />} />
            <Route path="/reports/:projectId/:reportId/sources" element={<SourcesPage />} />
            {/* MarcusAI Workbench (MW-4) — company-scoped fix queue, replaces the
                retired /recommendations flow. Scoped to .vb-root. */}
            <Route path="/reports/:projectId/workbench" element={<WorkbenchPage />} />
            <Route path="/reports/:projectId/:reportId/workbench" element={<WorkbenchPage />} />
            {/* Virtual Buyer (aits-2.0 design-system pilot, scoped to .vb-root) */}
            <Route path="virtual-buyer" element={<VbLayout />}>
              <Route index element={<BuyerOverviewPage />} />
              <Route path="new" element={<BuyerCreatePage />} />
              <Route path=":buyerId" element={<BuyerDetailPage />} />
              <Route path=":buyerId/run/:analysisId" element={<RunPage />} />
              <Route path=":buyerId/verdict/:analysisId" element={<VerdictPage />} />
            </Route>
            {/* More protected nested routes */}
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
    </BannerContext.Provider>
  );
}
export default App;
